// AlokCRM — Schedule Manager  (ported from the Field Tracker's Schedule.js)
// ==========================================================================
// Drop into the CRM frontend (e.g. src/pages/ScheduleManager.jsx) and add a
// "Schedule" route inside the CRM Layout. Renders the planning panel + Leaflet
// map (the CRM Layout supplies the left sidebar).
//
//   npm i leaflet         // already used by the tracker
//
// Data sources (all same-origin CRM, https):
//   GET  /api/v1/schedules/salespeople        -> all emp codes from Team
//   GET  /api/v1/schedules/company-search?q=  -> CRM accounts
//   POST /api/v1/schedules/send               -> forwards to tracker (phone)
//   GET  /api/v1/schedules                    -> saved schedules (from tracker)
// Geocoding stays client-side via Nominatim, exactly like your tracker page.

import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = '/api/v1/schedules';            // same-origin CRM (https)

// "2026-07-11" -> "11 Jul" for the recently-used origin list
const fmtUsedOn = d => {
  if (!d) return '';
  const parts = String(d).split('-');           // [yyyy, mm, dd]
  const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mi = parseInt(parts[1], 10);
  return names[mi] ? `${parseInt(parts[2], 10)} ${names[mi]}` : String(d);
};

export default function ScheduleManager() {
  const [spList, setSpList] = useState([]);
  const [spCode, setSpCode] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [stops, setStops] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [pendingStop, setPendingStop] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isManual, setIsManual] = useState(false);
  const [manualAddr, setManualAddr] = useState('');
  const [formData, setFormData] = useState({ time: '09:00', person: '', phone: '', notes: '' });
  const [toast, setToast] = useState({ msg: '', color: '', show: false });
  const [saving, setSaving] = useState(false);
  const [savedSchedules, setSavedSchedules] = useState({});
  const [showSaved, setShowSaved] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [startQuery, setStartQuery] = useState('');
  const [startSuggestions, setStartSuggestions] = useState([]);
  const [showStartSug, setShowStartSug] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  // recently-used origins for the selected SP (read-only, from saved schedules)
  const [recentOrigins, setRecentOrigins] = useState([]);
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [showAddrSug, setShowAddrSug] = useState(false);

  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markersRef = useRef([]);
  const searchMarkerRef = useRef(null);
  const routeLineRef = useRef(null);
  const centerMarkerRef = useRef(null);
  const startSearchTimeout = useRef(null);
  const companySearchTimeout = useRef(null);
  const addrSearchTimeout = useRef(null);

  useEffect(() => {
    if (mapRef.current && !mapInst.current) {
      const map = L.map(mapRef.current, { center: [19.076, 72.8777], zoom: 12 });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors', maxZoom: 19,
      }).addTo(map);
      mapInst.current = map;
    }
    return () => { if (mapInst.current) { mapInst.current.remove(); mapInst.current = null; } };
  }, []);

  useEffect(() => { loadSchedules(); loadSalespeople(); }, []);

  async function loadSalespeople() {
    try {
      const res = await fetch(`${API}/salespeople`, { headers: authHeaders() });
      const data = await res.json();
      setSpList(Array.isArray(data) ? data : []);
    } catch (e) { setSpList([]); }
  }

  function authHeaders() {
    return { 'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}` };
  }

  function showToast(msg, color = '#1a6fc4') {
    setToast({ msg, color, show: true });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 2500);
  }

  // ---- recently-used starting points for the selected SP ----
  // Refreshed whenever the salesperson changes. Never auto-fills the field —
  // the coordinator still has to pick one or type their own.
  useEffect(() => {
    let alive = true;
    setRecentOrigins([]);
    if (!spCode) return undefined;
    fetch(`${API}/recent-origins?sp_code=${encodeURIComponent(spCode)}&limit=6`,
      { headers: authHeaders() })
      .then(r => { if (!r.ok) throw new Error('recent-origins'); return r.json(); })
      .then(j => { if (alive && Array.isArray(j)) setRecentOrigins(j); })
      .catch(() => { if (alive) setRecentOrigins([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spCode]);

  // recent origins matching whatever has been typed so far
  const filteredRecent = recentOrigins.filter(o => {
    const q = startQuery.trim().toLowerCase();
    if (!q) return true;
    return (o.name || '').toLowerCase().includes(q)
      || (o.address || '').toLowerCase().includes(q);
  });

  function selectRecentOrigin(o) {
    // same shape the geocode path produces, so everything downstream
    // (save, chain route, Save & Send) is unchanged
    setStartQuery(o.name); setShowStartSug(false); setStartSuggestions([]);
    setStartPoint({ name: o.name, address: o.address || '', lat: o.lat, lng: o.lng });
    dropCenterPin(o.lat, o.lng, o.name);
    showToast('Starting point set: ' + o.name);
  }

  // ---- starting point (center) — Nominatim ----
  function onStartInput(q) {
    setStartQuery(q);
    if (startSearchTimeout.current) clearTimeout(startSearchTimeout.current);
    setShowStartSug(true);                    // keep the recent list visible while typing
    if (q.length < 2) { setStartSuggestions([]); return; }
    startSearchTimeout.current = setTimeout(() => fetchStartSuggestions(q), 400);
  }
  async function fetchStartSuggestions(q) {
    try {
      const res = await fetch(
        `${API}/geocode-search?q=${encodeURIComponent(q)}&limit=5`,
        { headers: authHeaders() });
      setStartSuggestions(await res.json()); setShowStartSug(true);
    } catch (e) { setStartSuggestions([]); }
  }
  function selectStartPoint(item) {
    const lat = parseFloat(item.lat), lng = parseFloat(item.lon);
    const name = item.display_name.split(',')[0];
    setStartQuery(name); setShowStartSug(false); setStartSuggestions([]);
    const sp = { name, lat, lng };
    setStartPoint(sp); dropCenterPin(lat, lng, name);
    showToast('Starting point set: ' + name);
  }
  function dropCenterPin(lat, lng, name) {
    const map = mapInst.current; if (!map) return;
    if (centerMarkerRef.current) map.removeLayer(centerMarkerRef.current);
    const icon = L.divIcon({
      className: '',
      html: `<div style="display:flex;flex-direction:column;align-items:center">
        <div style="width:32px;height:32px;border-radius:50%;background:#fff;border:3px solid #1a6fc4;box-shadow:0 2px 12px rgba(26,111,196,0.5);display:flex;align-items:center;justify-content:center;font-size:16px;">🏠</div>
        <div style="background:#1a6fc4;color:#fff;font-size:9px;font-weight:600;padding:2px 7px;border-radius:3px;margin-top:2px;white-space:nowrap;max-width:90px;overflow:hidden;text-overflow:ellipsis;">${name}</div>
      </div>`,
      iconSize: [32, 50], iconAnchor: [16, 16] });
    centerMarkerRef.current = L.marker([lat, lng], { icon, zIndexOffset: 1000 })
      .addTo(map).bindPopup(`<b>Starting Point</b><br/>${name}`);
    map.setView([lat, lng], 13);
  }
  function clearStartPoint() {
    setStartPoint(null); setStartQuery('');
    if (centerMarkerRef.current && mapInst.current) {
      mapInst.current.removeLayer(centerMarkerRef.current); centerMarkerRef.current = null;
    }
    drawChainRoute(stops, null);
  }

  // ---- company search — CRM accounts + Nominatim addresses ----
  function onSearchInput(q) {
    setSearchQuery(q);
    if (q.length < 1) { setSuggestions([]); setShowSug(false); return; }
    if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
    companySearchTimeout.current = setTimeout(() => fetchSuggestions(q), 300);
  }
  async function fetchSuggestions(q) {
    let crm = [];
    try {
      const res = await fetch(`${API}/company-search?q=${encodeURIComponent(q)}`, { headers: authHeaders() });
      crm = await res.json();
    } catch (e) {}
    setSuggestions(Array.isArray(crm) ? crm.slice(0, 4) : []);
    setShowSug(true);
    if (q.length >= 3) {
      try {
        const res = await fetch(
          `${API}/geocode-search?q=${encodeURIComponent(q)}&limit=4`,
          { headers: authHeaders() });
        const data = await res.json();
        const addrs = data.map(it => ({
          name: it.display_name.split(',')[0],
          address: it.display_name.split(',').slice(1, 3).join(','),
          lat: parseFloat(it.lat), lng: parseFloat(it.lon), isAddress: true,
        }));
        setSuggestions(prev => {
          const seen = new Set(prev.map(p => p.name.toLowerCase()));
          return [...prev, ...addrs.filter(a => !seen.has(a.name.toLowerCase()))].slice(0, 8);
        });
        setShowSug(true);
      } catch (e) {}
    }
  }
  function selectCompany(company) {
    setSearchQuery(company.name); setShowSug(false); setSuggestions([]);
    const hasSaved = !!(company.address && company.lat && company.lng);
    setIsManual(!hasSaved && !(company.lat && company.lng));
    setManualAddr(company.address || '');
    setPendingStop({ ...company, addr: company.address || '', address: company.address || '' });
    setFormData({ time: '09:00', person: company.contact || '', phone: company.phone || '', notes: '' });
    setShowForm(true);
    if (company.lat && company.lng) dropSearchPin(company.lat, company.lng, company.name);
    showToast(hasSaved ? 'Saved address loaded — pin dropped' : 'Confirm details', '#378ADD');
  }
  function selectManual(q) {
    setShowSug(false); setSuggestions([]); setIsManual(true); setManualAddr('');
    setPendingStop({ name: q, addr: '', lat: null, lng: null });
    setFormData({ time: '09:00', person: '', phone: '', notes: '' });
    setShowForm(true); showToast('Fill in address manually', '#6b7280');
  }
  function onAddrInput(q) {
    setManualAddr(q);
    setPendingStop(p => (p ? { ...p, lat: null, lng: null } : p));
    if (addrSearchTimeout.current) clearTimeout(addrSearchTimeout.current);
    if (q.length < 3) { setAddrSuggestions([]); setShowAddrSug(false); return; }
    addrSearchTimeout.current = setTimeout(() => fetchAddrSuggestions(q), 400);
  }
  async function fetchAddrSuggestions(q) {
    try {
      const res = await fetch(`${API}/geocode-search?q=${encodeURIComponent(q)}&limit=5`, { headers: authHeaders() });
      const data = await res.json();
      setAddrSuggestions(Array.isArray(data) ? data : []);
      setShowAddrSug(true);
    } catch (e) { setAddrSuggestions([]); }
  }
  function selectAddrSuggestion(it) {
    const lat = parseFloat(it.lat), lng = parseFloat(it.lon);
    setManualAddr(it.display_name);
    setShowAddrSug(false); setAddrSuggestions([]);
    setPendingStop(p => (p ? { ...p, lat, lng } : p));
    dropSearchPin(lat, lng, (pendingStop && pendingStop.name) || it.display_name.split(',')[0]);
    showToast('Exact address selected - pin dropped', '#166534');
  }
  function dropSearchPin(lat, lng, name) {
    const map = mapInst.current; if (!map || !lat || !lng) return;
    if (searchMarkerRef.current) map.removeLayer(searchMarkerRef.current);
    const icon = L.divIcon({ className: '',
      html: `<div style="width:28px;height:28px;border-radius:50%;background:#378ADD;color:#fff;display:flex;align-items:center;justify-content:center;border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);font-size:14px;font-weight:700;">${stops.length + 1}</div>`,
      iconSize: [28, 28], iconAnchor: [14, 14] });
    searchMarkerRef.current = L.marker([lat, lng], { icon }).addTo(map).bindPopup(name).openPopup();
    map.setView([lat, lng], 14);
  }
  async function geocodeAddress(addr) {
    try {
      const aq = /mumbai|india/i.test(addr) ? addr : (addr + ', Mumbai, India');
      const res = await fetch(`${API}/geocode?q=${encodeURIComponent(aq)}`, { headers: authHeaders() });
      const data = await res.json();
      if (data && data.found) return { lat: data.lat, lng: data.lng };
    } catch (e) {}
    return null;
  }
  function cancelStop() {
    setShowForm(false); setPendingStop(null); setSearchQuery(''); setIsManual(false); setManualAddr('');
    if (searchMarkerRef.current && mapInst.current) { mapInst.current.removeLayer(searchMarkerRef.current); searchMarkerRef.current = null; }
  }
  async function confirmStop() {
    if (!pendingStop) return;
    let lat = pendingStop.lat, lng = pendingStop.lng;
    const addr = isManual ? manualAddr : (pendingStop.address || pendingStop.addr || '');
    if ((!lat || !lng) && addr) { const geo = await geocodeAddress(addr); if (geo) { lat = geo.lat; lng = geo.lng; } }
    const newStop = { ...pendingStop, addr, lat, lng, ...formData, id: Date.now() };
    const newStops = [...stops, newStop];
    setStops(newStops);
    if (searchMarkerRef.current && mapInst.current) { mapInst.current.removeLayer(searchMarkerRef.current); searchMarkerRef.current = null; }
    setShowForm(false); setPendingStop(null); setSearchQuery(''); setIsManual(false); setManualAddr('');
    addMarkerToMap(newStop, newStops.length - 1, newStops);
    showToast(`Stop ${newStops.length} added!`);
  }
  function addMarkerToMap(stop, idx, allStops) {
    const map = mapInst.current; if (!map || !stop.lat || !stop.lng) return;
    const icon = L.divIcon({ className: '',
      html: `<div style="width:26px;height:26px;border-radius:50%;background:#1a6fc4;color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${idx + 1}</div>`,
      iconSize: [26, 26], iconAnchor: [13, 13] });
    const popup = `<b>${stop.name}</b><br/>${stop.addr || ''}<br/><span style="color:#1a6fc4;font-weight:600;">${stop.time}</span>${stop.person ? '<br/>' + stop.person : ''}${stop.phone ? '<br/>' + stop.phone : ''}${stop.notes ? '<br/><i>' + stop.notes + '</i>' : ''}`;
    const marker = L.marker([stop.lat, stop.lng], { icon }).addTo(map).bindPopup(popup);
    markersRef.current.push({ id: stop.id, marker });
    drawChainRoute(allStops, startPoint);
    try {
      var fp = (allStops || []).filter(function(s){ return s.lat && s.lng; }).map(function(s){ return [s.lat, s.lng]; });
      if (startPoint && startPoint.lat && startPoint.lng) fp.push([startPoint.lat, startPoint.lng]);
      if (fp.length) map.fitBounds(L.latLngBounds(fp), { padding: [60, 60], maxZoom: 15 });
    } catch (e) {}
  }
  function drawChainRoute(stopsToUse, sp) {
    const map = mapInst.current; if (!map) return;
    if (routeLineRef.current) { try { map.removeLayer(routeLineRef.current); } catch (e) {} routeLineRef.current = null; }
    const valid = (stopsToUse || stops).filter(s => s.lat && s.lng);
    if (valid.length === 0) return;
    const points = [];
    if (sp) points.push([sp.lat, sp.lng]);
    valid.forEach(s => points.push([s.lat, s.lng]));
    if (sp && valid.length > 0) points.push([sp.lat, sp.lng]);
    if (points.length < 2) return;
    const mainPoints = sp ? points.slice(0, points.length - 1) : points;
    const mainLine = L.polyline(mainPoints, { color: '#1a6fc4', weight: 2.5, opacity: 0.85 }).addTo(map);
    if (sp && valid.length > 0) {
      const ret = L.polyline([[valid[valid.length - 1].lat, valid[valid.length - 1].lng], [sp.lat, sp.lng]],
        { color: '#1a6fc4', weight: 1.5, opacity: 0.5, dashArray: '6,6' }).addTo(map);
      routeLineRef.current = L.layerGroup([mainLine, ret]);
    } else { routeLineRef.current = mainLine; }
    if (routeLineRef.current && !routeLineRef.current._map) routeLineRef.current.addTo(map);
  }
  function sortByTime() {
    const sorted = [...stops].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    clearMarkers(); setStops(sorted); sorted.forEach((s, i) => addMarkerToMap(s, i, sorted));
    showToast('Sorted by visit time!');
  }
  function optimizeRoute() {
    if (stops.length < 2) { showToast('Add at least 2 stops first', '#f59e0b'); return; }
    const valid = stops.filter(s => s.lat && s.lng);
    if (valid.length < 2) { showToast('Need at least 2 stops with location', '#f59e0b'); return; }
    const n = valid.length, visited = new Array(n).fill(false), order = [0]; visited[0] = true;
    for (let i = 1; i < n; i++) {
      const last = order[order.length - 1]; let nearest = -1, minD = Infinity;
      for (let j = 0; j < n; j++) if (!visited[j]) {
        const dx = valid[last].lat - valid[j].lat, dy = valid[last].lng - valid[j].lng;
        const d = Math.sqrt(dx * dx + dy * dy); if (d < minD) { minD = d; nearest = j; }
      }
      order.push(nearest); visited[nearest] = true;
    }
    const opt = order.map(i => valid[i]);
    clearMarkers(); setStops(opt); opt.forEach((s, i) => addMarkerToMap(s, i, opt));
    showToast('Route optimized — shortest path!');
  }
  function clearMarkers() {
    const map = mapInst.current; if (!map) return;
    markersRef.current.forEach(({ marker }) => { try { map.removeLayer(marker); } catch (e) {} });
    markersRef.current = [];
    if (routeLineRef.current) {
      try {
        if (routeLineRef.current.getLayers) routeLineRef.current.getLayers().forEach(l => map.removeLayer(l));
        else map.removeLayer(routeLineRef.current);
      } catch (e) {}
      routeLineRef.current = null;
    }
  }
  function removeStop(id) {
    const ns = stops.filter(s => s.id !== id);
    setStops(ns); clearMarkers(); ns.forEach((s, i) => addMarkerToMap(s, i, ns));
  }
  function highlightStop(stop) {
    const f = markersRef.current.find(m => m.id === stop.id);
    if (f && mapInst.current) { mapInst.current.setView([stop.lat, stop.lng], 15); f.marker.openPopup(); }
  }
  function clearAll() {
    clearMarkers(); setStops([]); setShowForm(false); setPendingStop(null); setSearchQuery(''); clearStartPoint();
    if (searchMarkerRef.current && mapInst.current) { mapInst.current.removeLayer(searchMarkerRef.current); searchMarkerRef.current = null; }
  }

  // ---- save & send (forwarded by CRM to the tracker -> SP phone) ----
  function handleSaveClick() {
    if (!spCode) { showToast('Select a Sales Person', '#ef4444'); return; }
    if (!date) { showToast('Select a date', '#ef4444'); return; }
    if (stops.length === 0) { showToast('Add at least one stop', '#ef4444'); return; }
    setShowConfirm(true);
  }
  async function saveSchedule() {
    setShowConfirm(false); setSaving(true);
    try {
      const res = await fetch(`${API}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          sp_code: spCode, date,
          start_point: startPoint && startPoint.lat && startPoint.lng ? {
            name: startPoint.name || startQuery || 'Start',
            address: startPoint.address || '',
            lat: startPoint.lat, lng: startPoint.lng,
          } : null,
          visits: stops.map((s, i) => ({
            id: i + 1, company: s.name, address: s.addr || s.address || '',
            time: s.time, phone: s.phone || '', contact_person: s.person || '',
            notes: s.notes || '', status: 'Pending',
          })),
        }),
      });
      if (!res.ok) throw new Error('save failed');
      showToast(`✓ Sent to ${spName}! They will see it on their phone shortly.`, '#166534');
      loadSchedules();
    } catch (e) { showToast('Error sending — check tracker connection', '#ef4444'); }
    setSaving(false);
  }
  async function loadSchedules() {
    try {
      const res = await fetch(`${API}`, { headers: authHeaders() });
      setSavedSchedules(await res.json());
    } catch (e) {}
  }

  const inp = { width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, outline: 'none', background: '#fff', color: '#111827' };
  const lbl = { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5, display: 'block' };
  const btnBlue = { background: '#1a6fc4', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 };
  const btnOut = { background: '#fff', color: '#1a6fc4', border: '1px solid #1a6fc4', borderRadius: 8, padding: '7px 12px', fontSize: 12, cursor: 'pointer' };
  const btnGreen = { background: '#166534', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 };
  const spName = spList.find(s => (s.ecode || s.code) === spCode)?.name || '';

  // group SPs by role for the dropdown
  const grouped = spList.reduce((acc, s) => {
    const role = s.role || 'Salespeople';
    (acc[role] = acc[role] || []).push(s); return acc;
  }, {});

  return (
    <div style={{ height: '100%', display: 'flex', fontFamily: '-apple-system,sans-serif', overflow: 'hidden' }}>
      <div style={{ width: 310, background: '#fff', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Schedule Manager</div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Center → Stop 1 → Stop 2 → back to center</div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* SP + date */}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1.5 }}>
              <span style={lbl}>Sales Person · from Team</span>
              <select value={spCode} onChange={e => setSpCode(e.target.value)} style={inp}>
                <option value="">Select…</option>
                {Object.keys(grouped).map(role => (
                  <optgroup key={role} label={role}>
                    {grouped[role].map(s => (
                      <option key={s.ecode || s.code} value={s.ecode || s.code}>
                        {(s.ecode || s.code)} · {s.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <span style={lbl}>Date</span>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
            </div>
          </div>

          {/* starting point */}
          <div style={{ position: 'relative' }}>
            <span style={lbl}>Starting Point — center of route</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={startQuery}
                onChange={e => onStartInput(e.target.value)}
                onFocus={() => setShowStartSug(true)}
                onBlur={() => setTimeout(() => setShowStartSug(false), 180)}
                placeholder={recentOrigins.length ? 'Pick a recent start, or type area, station, office…' : 'Type area, station, office…'}
                style={inp}
              />
              {startPoint && <button onClick={clearStartPoint} style={{ ...btnOut, padding: '0 10px' }}>×</button>}
            </div>
            {showStartSug && (filteredRecent.length > 0 || startSuggestions.length > 0) && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 2, maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,.08)' }}>

                {/* recently used by this SP */}
                {filteredRecent.length > 0 && (
                  <>
                    <div style={{ padding: '5px 10px', fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', background: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                      Recently used
                    </div>
                    {filteredRecent.map((o, i) => (
                      <div
                        key={`recent-${i}`}
                        onMouseDown={() => selectRecentOrigin(o)}
                        style={{ padding: '7px 10px', fontSize: 11, cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 7 }}
                      >
                        <span style={{ flexShrink: 0 }}>🏠</span>
                        <span style={{ flex: 1, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</span>
                        {o.last_used && <span style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0 }}>{fmtUsedOn(o.last_used)}</span>}
                      </div>
                    ))}
                  </>
                )}

                {/* free-text geocode search (unchanged) */}
                {startSuggestions.length > 0 && (
                  <>
                    <div style={{ padding: '5px 10px', fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', background: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                      Search results
                    </div>
                    {startSuggestions.map((it, i) => (
                      <div key={i} onMouseDown={() => selectStartPoint(it)} style={{ padding: '7px 10px', fontSize: 11, cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                        {it.display_name}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* company search */}
          <div style={{ position: 'relative' }}>
            <span style={lbl}>Company Name — type to search</span>
            <input value={searchQuery} onChange={e => onSearchInput(e.target.value)} placeholder="e.g. Wilo, KSB, Caprari or any address…" style={inp} />
            {showSug && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 2, maxHeight: 220, overflowY: 'auto' }}>
                {suggestions.map((c, i) => (
                  <div key={i} onClick={() => selectCompany(c)} style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {c.name}
                      {c.saved && <span style={{ marginLeft: 6, fontSize: 9, color: '#166534', background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>saved</span>}
                    </div>
                    <div style={{ fontSize: 10, color: '#6b7280' }}>{c.address || 'No saved address — enter manually'}</div>
                  </div>
                ))}
                {searchQuery && (
                  <div onClick={() => selectManual(searchQuery)} style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 11, color: '#1a6fc4' }}>
                    + Add "{searchQuery}" manually
                  </div>
                )}
              </div>
            )}
          </div>

          {/* instant form */}
          {showForm && pendingStop && (
            <div style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#1a6fc4', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{stops.length + 1}</div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{pendingStop.name}</span>
              </div>
              {isManual ? (
                <div style={{ marginBottom: 10 }}>
                  <span style={{ ...lbl, fontSize: 10 }}>Address</span>
                  <input value={manualAddr} onChange={e => onAddrInput(e.target.value)} onBlur={() => setTimeout(() => setShowAddrSug(false), 250)} placeholder="Type full address..." style={inp} autoFocus />
                  {pendingStop && pendingStop.lat && pendingStop.lng ? (
                    <div style={{ fontSize: 10, color: '#166534', marginTop: 3 }}>Exact location locked</div>
                  ) : null}
                  {showAddrSug && addrSuggestions.length > 0 && (
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 4, maxHeight: 180, overflowY: 'auto' }}>
                      {addrSuggestions.map((it, i) => (
                        <div key={i} onMouseDown={() => selectAddrSuggestion(it)} style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#111827' }}>{it.display_place || it.display_name.split(',')[0]}</div>
                          <div style={{ fontSize: 10, color: '#6b7280' }}>{it.display_address || it.display_name.split(',').slice(1, 4).join(',')}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: '#6b7280', padding: '4px 8px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb', marginBottom: 10 }}>
                  {pendingStop.address || pendingStop.addr}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ ...lbl, fontSize: 10 }}>Visit Time</span>
                  <input type="time" value={formData.time} onChange={e => setFormData(f => ({ ...f, time: e.target.value }))} style={inp} />
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ ...lbl, fontSize: 10 }}>Contact Person</span>
                  <input value={formData.person} onChange={e => setFormData(f => ({ ...f, person: e.target.value }))} placeholder="Name" style={inp} />
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ ...lbl, fontSize: 10 }}>Phone</span>
                <input value={formData.phone} onChange={e => setFormData(f => ({ ...f, phone: e.target.value }))} placeholder="+91 98765 43210" style={inp} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ ...lbl, fontSize: 10 }}>Notes</span>
                <textarea value={formData.notes} onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Ask about pump order..." rows={2} style={{ ...inp, resize: 'none', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={cancelStop} style={{ ...btnOut, flex: 1 }}>Cancel</button>
                <button onClick={confirmStop} style={{ ...btnBlue, flex: 1 }}>Add Stop</button>
              </div>
            </div>
          )}

          {/* stops */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={lbl}>Stops ({stops.length})</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {stops.length > 1 && <button onClick={sortByTime} style={{ background: 'none', border: '1px solid #1a6fc4', color: '#1a6fc4', fontSize: 10, cursor: 'pointer', borderRadius: 6, padding: '2px 8px' }}>Sort by time</button>}
                {stops.length > 0 && <button onClick={clearAll} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>Clear all</button>}
              </div>
            </div>
            {stops.length === 0 ? (
              <div style={{ padding: 12, background: '#f9fafb', borderRadius: 8, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                {startPoint ? 'Search a company to add stops' : 'Set starting point first'}
              </div>
            ) : stops.map((stop, i) => (
              <div key={stop.id} onClick={() => highlightStop(stop)} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: '#f9fafb', borderRadius: 8, marginBottom: 6, cursor: 'pointer', border: '1px solid #e5e7eb' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#1a6fc4', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stop.name}</div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{stop.addr || stop.address}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                    <span style={{ fontSize: 10, color: '#1a6fc4', fontWeight: 600 }}>{stop.time}</span>
                    {stop.person && <span style={{ fontSize: 10, color: '#6b7280' }}>{stop.person}</span>}
                  </div>
                  {stop.notes && <div style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic', marginTop: 1 }}>{stop.notes.slice(0, 40)}{stop.notes.length > 40 ? '...' : ''}</div>}
                </div>
                <button onClick={e => { e.stopPropagation(); removeStop(stop.id); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: 0, flexShrink: 0 }}>×</button>
              </div>
            ))}
          </div>

          {/* saved */}
          <div>
            <button onClick={() => { setShowSaved(!showSaved); loadSchedules(); }} style={{ ...btnOut, width: '100%', fontSize: 11 }}>
              {showSaved ? 'Hide' : 'View'} Saved Schedules ({Object.keys(savedSchedules).length})
            </button>
            {showSaved && (
              <div style={{ marginTop: 8 }}>
                {Object.keys(savedSchedules).length === 0 ? (
                  <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', padding: 8 }}>No schedules saved yet</div>
                ) : Object.values(savedSchedules).map((sch, i) => (
                  <div key={i} style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 10, marginBottom: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{spList.find(s => (s.ecode || s.code) === sch.sp_code)?.name ? `${spList.find(s => (s.ecode || s.code) === sch.sp_code).name} · ${sch.sp_code}` : sch.sp_code}</span>
                      <span style={{ fontSize: 10, color: '#6b7280' }}>{sch.date} · {sch.visits.length} visits</span>
                    </div>
                    {sch.visits.map((v, j) => (
                      <div key={j} style={{ fontSize: 10, color: '#6b7280', padding: '2px 0', borderBottom: j < sch.visits.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                        <span style={{ color: '#1a6fc4', fontWeight: 600 }}>{v.time}</span> — {v.company}{v.contact_person && <span> · {v.contact_person}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {showConfirm && (
          <div style={{ margin: '0 16px 12px', background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 3 }}>Send schedule to {spName}?</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
              {spName} will see {stops.length} visit{stops.length !== 1 ? 's' : ''} on their phone shortly.
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setShowConfirm(false)} style={{ ...btnOut, flex: 1, fontSize: 11 }}>Cancel</button>
              <button onClick={saveSchedule} style={{ ...btnGreen, flex: 1, fontSize: 11 }}>Yes, Send Now</button>
            </div>
          </div>
        )}

        <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
          <button onClick={optimizeRoute} style={{ ...btnOut, flex: 1, fontSize: 11 }}>Optimize Route</button>
          <button onClick={handleSaveClick} disabled={saving} style={{ ...btnBlue, flex: 1, fontSize: 11, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Sending...' : 'Save & Send to SP'}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, position: 'relative' }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }}>
          <style>{`.leaflet-popup-content-wrapper{border-radius:10px!important;font-size:12px!important;}`}</style>
        </div>
        {toast.show && (
          <div style={{ position: 'absolute', bottom: 16, right: 16, background: toast.color, color: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
            {toast.msg}
          </div>
        )}
      </div>
    </div>
  );
}
